import {
  type DocumentTableRow,
  type SensorSearchItem,
  useRecentItems,
  useSensorSearch,
} from 'features/knowledgeDocumentViewer'
import { useEnabledDocumentSearch } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useEnabledDocumentSearch'
import {
  type DocumentSearchResult,
  type Highlight,
  type SearchEngine,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { isAiDocumentSearchEngine } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchMode'
import { transformSystemDocumentToTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import {
  orderDocumentsById,
  orderDocumentsByScoreDesc,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/sortDocumentSearchResults'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type {
  DocumentSearchMode,
  SearchHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { getSystemDocumentBqId } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  dedupeSystemDocumentsByDocumentId,
  systemDocumentsByDocumentIds,
} from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import {
  unscopePageIdFromPipelineConfig,
  unscopeSearchDocumentIdForHydration,
} from 'features/knowledgeDocumentViewer/utils/pipelineConfigScopedIds'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import React, { createContext, useContext, useMemo } from 'react'
import { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'
import { KnowledgeDocumentEntry, RolodexEntryType } from 'types'

interface DocumentSearchDataContextType {
  documentResults: DocumentTableRow[]
  documentTotalCount: number
  isLoadingDocuments: boolean
  sensorResults: SensorSearchItem[]
  sensorTotalCount: number
  isLoadingSensors: boolean
  recentItems: ReturnType<typeof useRecentItems>['recentItems']
  addRecentItem: ReturnType<typeof useRecentItems>['addRecentItem']
  elasticsearchHighlightsMap: Map<string, Highlight[]>
  rawSearchData: DocumentSearchResult | undefined
  searchEngine: SearchEngine
  searchLatencyMs: number | undefined
  searchedSystemDocuments: SystemDocumentEntry[] | undefined
  refreshDocumentSearch: () => void
}

interface DocumentSearchDataProviderProps {
  children: React.ReactNode
  siteId: string
  tabId?: string
  searchQuery: string
  contentFilter: DocumentsViewerContentFilter
  searchMode: DocumentSearchMode
  /** When set, document list is filtered to these IDs (no search). */
  linkedDocIds?: string[]
  /** When linkedDocIds is empty, resolve doc IDs from knowledge docs in this unit. */
  unitId?: string
  browseAll?: boolean
  agenticSearch?: SearchHistoryItem['searchState']['agenticSearch']
}

const DocumentSearchDataContext = createContext<
  DocumentSearchDataContextType | undefined
>(undefined)

const isLinkedDocsMode = (linkedDocIds?: string[], unitId?: string) =>
  (linkedDocIds && linkedDocIds.length > 0) || !!unitId

export const DocumentSearchDataProvider: React.FC<
  DocumentSearchDataProviderProps
> = ({
  children,
  siteId,
  tabId,
  searchQuery,
  contentFilter,
  searchMode,
  linkedDocIds,
  unitId,
  browseAll = false,
  agenticSearch,
}) => {
  const {
    systemDocumentEntries: allSystemDocuments,
    isLoadingDocuments: isLoadingAllDocs,
  } = useKnowledgeDocumentDataContext()
  const {
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    selectedPipelineConfigId,
  } = usePipelineConfig()
  const linkedMode = isLinkedDocsMode(linkedDocIds, unitId)
  const {
    rawDocumentSearchData,
    isLoadingSearch,
    searchEngine,
    searchLatencyMs,
    refreshDocumentSearch,
  } = useEnabledDocumentSearch({
    siteId,
    tabId,
    searchQuery,
    contentFilter,
    searchMode,
    linkedMode,
    browseAll,
    agenticSearch,
  })

  const knowledgeDocLoadOptions = useMemo(() => {
    if (
      isPipelineConfigScopeLoading ||
      !unitId ||
      !siteId ||
      (linkedDocIds && linkedDocIds.length > 0)
    ) {
      return undefined
    }

    return {
      type: [RolodexEntryType.KNOWLEDGE_DOCUMENT],
      tags: getPipelineConfigScopedTags({ site: siteId, unit: unitId }),
      limit: 1000000,
    }
  }, [
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    linkedDocIds,
    siteId,
    unitId,
  ])

  const { data: knowledgeDocsForUnit, isLoading: isLoadingUnit } =
    useGetRolodexEntriesByLoadOptions<KnowledgeDocumentEntry[]>(
      knowledgeDocLoadOptions
    )

  const resolvedLinkedIds = useMemo(() => {
    if (linkedDocIds && linkedDocIds.length > 0) return linkedDocIds
    if (!knowledgeDocsForUnit) return []
    const ids = new Set<string>()
    for (const entry of knowledgeDocsForUnit) {
      const systemDocTag = entry.entry.tags['system-document']
      if (systemDocTag) ids.add(systemDocTag)
    }
    return Array.from(ids)
  }, [linkedDocIds, knowledgeDocsForUnit])

  // TEMPORARY: prod search still returns DOC-1_{configHash}. Unscope to bare
  // BQ document_id for dual-read hydration until power-backend #161 ships.
  const recommendedDocumentIds = useMemo(() => {
    if (linkedMode) return resolvedLinkedIds
    if (browseAll) return []
    return (
      rawDocumentSearchData?.results.map((result) =>
        unscopeSearchDocumentIdForHydration(
          result.document_id,
          selectedPipelineConfigId
        )
      ) ?? []
    )
  }, [
    linkedMode,
    browseAll,
    resolvedLinkedIds,
    rawDocumentSearchData,
    selectedPipelineConfigId,
  ])

  // Linked mode still passes system-document entry ids. Search hydrates only via
  // data.document_id (bare BQ ids after power-backend #161 / FE bridge unscope).
  // Drop the parallel entry-id load — it was a wasted Rolodex round-trip once ids
  // are unscoped / UUID, and doubled Rolodex contention on every search.
  const searchedByEntryIdLoadOptions = useMemo(() => {
    if (
      !linkedMode ||
      isPipelineConfigScopeLoading ||
      recommendedDocumentIds.length === 0
    ) {
      return undefined
    }
    return {
      type: [RolodexEntryType.SYSTEM_DOCUMENT],
      ids: recommendedDocumentIds,
      tags: getPipelineConfigScopedTags({ site: siteId }),
      limit: 200,
    }
  }, [
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    linkedMode,
    recommendedDocumentIds,
    siteId,
  ])

  const searchedByBqFieldLoadOptions = useMemo(() => {
    if (
      linkedMode ||
      isPipelineConfigScopeLoading ||
      recommendedDocumentIds.length === 0
    ) {
      return undefined
    }
    return systemDocumentsByDocumentIds(
      recommendedDocumentIds,
      getPipelineConfigScopedTags({ site: siteId }),
      200
    )
  }, [
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    linkedMode,
    recommendedDocumentIds,
    siteId,
  ])

  const { data: searchedByEntryIdDocuments, isLoading: isLoadingByEntryId } =
    useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(
      searchedByEntryIdLoadOptions
    )

  const { data: searchedByBqFieldDocuments, isLoading: isLoadingByBqField } =
    useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(
      searchedByBqFieldLoadOptions
    )

  const searchedSystemDocuments = useMemo(() => {
    if (linkedMode) {
      return searchedByEntryIdDocuments
    }
    if (searchedByBqFieldDocuments === undefined) {
      return undefined
    }
    // Site-only loads (no active/preview config) can return one system-document
    // per pipeline-config for the same BQ document_id — collapse to one row.
    return dedupeSystemDocumentsByDocumentId(searchedByBqFieldDocuments)
  }, [linkedMode, searchedByBqFieldDocuments, searchedByEntryIdDocuments])

  const isLoadingSystemDocs = linkedMode
    ? isLoadingByEntryId
    : isLoadingByBqField

  const documentResults = useMemo(() => {
    if (browseAll) {
      return dedupeSystemDocumentsByDocumentId(allSystemDocuments).map(
        transformSystemDocumentToTableRow
      )
    }

    if (!searchedSystemDocuments) return []

    const rows = searchedSystemDocuments.map(transformSystemDocumentToTableRow)

    if (linkedMode) {
      return orderDocumentsById(rows, recommendedDocumentIds)
    }

    if (isAiDocumentSearchEngine(searchEngine)) {
      // Agentic order is BQ / search ids; map to entry UUIDs for row ordering.
      const entryOrderIds = searchedSystemDocuments.map((entry) => {
        const bqId = getSystemDocumentBqId(entry)
        const searchIndex = recommendedDocumentIds.findIndex(
          (id) => id === bqId || id === entry.entry.id
        )
        return { entryId: entry.entry.id, searchIndex }
      })
      const orderedEntryIds = entryOrderIds
        .filter((row) => row.searchIndex >= 0)
        .sort((a, b) => a.searchIndex - b.searchIndex)
        .map((row) => row.entryId)
      return orderDocumentsById(rows, orderedEntryIds, {
        filterToOrderedIds: true,
      })
    }

    const scoresBySearchId = new Map<string, number>()
    for (const result of rawDocumentSearchData?.results ?? []) {
      scoresBySearchId.set(
        unscopeSearchDocumentIdForHydration(
          result.document_id,
          selectedPipelineConfigId
        ),
        result.score
      )
    }
    const scoresByEntryId = new Map<string, number>()
    for (const entry of searchedSystemDocuments) {
      const bqId = getSystemDocumentBqId(entry)
      const score =
        (bqId !== undefined ? scoresBySearchId.get(bqId) : undefined) ??
        scoresBySearchId.get(entry.entry.id)
      if (score !== undefined) {
        scoresByEntryId.set(entry.entry.id, score)
      }
    }
    return orderDocumentsByScoreDesc(rows, scoresByEntryId)
  }, [
    browseAll,
    allSystemDocuments,
    searchedSystemDocuments,
    rawDocumentSearchData,
    linkedMode,
    recommendedDocumentIds,
    searchEngine,
    selectedPipelineConfigId,
  ])
  const documentTotalCount = documentResults.length

  const isLoadingDocuments = browseAll
    ? isLoadingAllDocs
    : linkedMode
      ? isLoadingSystemDocs ||
        (!!unitId &&
          (!linkedDocIds || linkedDocIds.length === 0) &&
          isLoadingUnit)
      : isLoadingSearch || isLoadingSystemDocs

  const {
    sensors: sensorResults,
    totalCount: sensorTotalCount,
    isLoading: isLoadingSensors,
  } = useSensorSearch({
    siteId,
    searchText: contentFilter === 'sensors' ? searchQuery : '',
    enabled: contentFilter === 'sensors',
  })

  const { recentItems, addRecentItem } = useRecentItems(siteId)

  const elasticsearchHighlightsMap = useMemo(() => {
    if (linkedMode || browseAll) return new Map<string, Highlight[]>()
    const bySearchId = new Map<string, Highlight[]>()
    for (const result of rawDocumentSearchData?.results ?? []) {
      if (result.highlights && result.highlights.length > 0) {
        const bareDocumentId = unscopeSearchDocumentIdForHydration(
          result.document_id,
          selectedPipelineConfigId
        )
        bySearchId.set(
          bareDocumentId,
          result.highlights.map((highlight) => ({
            ...highlight,
            page_id:
              typeof highlight.page_id === 'string'
                ? unscopePageIdFromPipelineConfig(
                    highlight.page_id,
                    selectedPipelineConfigId
                  )
                : highlight.page_id,
          }))
        )
      }
    }
    // Consumers look up by DocumentTableRow.id (system-document entry UUID).
    const map = new Map<string, Highlight[]>()
    for (const entry of searchedSystemDocuments ?? []) {
      const bqId = getSystemDocumentBqId(entry)
      const highlights =
        (bqId !== undefined ? bySearchId.get(bqId) : undefined) ??
        bySearchId.get(entry.entry.id)
      if (highlights) {
        map.set(entry.entry.id, highlights)
      }
    }
    return map
  }, [
    linkedMode,
    browseAll,
    rawDocumentSearchData,
    searchedSystemDocuments,
    selectedPipelineConfigId,
  ])

  const contextValue = useMemo<DocumentSearchDataContextType>(
    () => ({
      documentResults,
      documentTotalCount,
      isLoadingDocuments,
      sensorResults,
      sensorTotalCount,
      isLoadingSensors,
      recentItems,
      addRecentItem,
      elasticsearchHighlightsMap,
      rawSearchData:
        linkedMode || browseAll ? undefined : rawDocumentSearchData,
      searchEngine,
      searchLatencyMs: linkedMode || browseAll ? undefined : searchLatencyMs,
      searchedSystemDocuments,
      refreshDocumentSearch,
    }),
    [
      documentResults,
      documentTotalCount,
      isLoadingDocuments,
      sensorResults,
      sensorTotalCount,
      isLoadingSensors,
      recentItems,
      addRecentItem,
      elasticsearchHighlightsMap,
      linkedMode,
      browseAll,
      rawDocumentSearchData,
      searchEngine,
      searchLatencyMs,
      searchedSystemDocuments,
      refreshDocumentSearch,
    ]
  )

  return (
    <DocumentSearchDataContext.Provider value={contextValue}>
      {children}
    </DocumentSearchDataContext.Provider>
  )
}

export const useDocumentSearchData = (): DocumentSearchDataContextType => {
  const context = useContext(DocumentSearchDataContext)
  if (context === undefined) {
    throw new Error(
      'useDocumentSearchData must be used within a DocumentSearchDataProvider'
    )
  }
  return context
}
